import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { deepMerge, getByPath } from "@/lib/objectPath";
import { useTheme } from "@/contexts/ThemeContext";
import { useCacheVersion } from "@/hooks/useCacheVersion";

// Defaults (mirror what's hard-coded). Keep in sync with the rest of the app.
const DEFAULT_THEME = {
  bg: "#050505",
  surface: "#0a0a0a",
  text: "#F2EBE5",
  muted: "#A19E9C",
  accent: "#C5A059",
};

const DEFAULT_CONTACT = {
  phone: "+212 6 61 49 13 49",
  phoneHref: "tel:+212661491349",
  whatsappHref: "https://wa.me/212661491349",
  email: "contact@kiwamistudios.com",
  emailHref: "mailto:contact@kiwamistudios.com",
  instagramHref: "https://www.instagram.com/kiwami_studios/",
  city_fr: "Local · International",
  city_en: "Local · International",
};

const DEFAULT_BRAND = {
  name: "Kiwami Studios & Co",
  baseline_fr: "L'image taillée avec précision. Du chantier à la lumière de studio.",
  baseline_en: "Imagery sculpted with precision. From construction site to studio light.",
};

// Section visibility defaults — all true
const DEFAULT_SECTIONS = {
  home_about: true,
  home_services: true,
  home_works: true,
  home_cta_band: true,
  service_other_services: true,
  service_portfolio: true,
};

const DEFAULT_HOME_STATS = {
  years: "12+",
  projects: "420",
  disciplines: "6",
};

const DEFAULTS = {
  theme: DEFAULT_THEME,
  contact: DEFAULT_CONTACT,
  brand: DEFAULT_BRAND,
  sections: DEFAULT_SECTIONS,
  home: { stats: DEFAULT_HOME_STATS },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SiteContentContext = createContext();

export const SiteContentProvider = ({ children }) => {
  const [data, setData] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const { mode } = useTheme();
  // Re-fetch site content whenever the global cache version changes (i.e.,
  // after any admin mutation that bumps it). This propagates marketing-image
  // edits, watermark settings, OG image swaps, etc. to active visitors on
  // their next page load without manual refresh.
  const cacheVersion = useCacheVersion();

  const fetchContent = useCallback(async () => {
    try {
      // Append the version as a cache-buster so neither browser nor CDN
      // returns a stale copy.
      const url = cacheVersion
        ? `${API}/site-content?v=${cacheVersion}`
        : `${API}/site-content`;
      const res = await axios.get(url);
      setData(res.data?.data || {});
      setUpdatedAt(res.data?.updated_at || null);
    } catch (err) {
      console.warn("site-content fetch failed", err?.message);
      setData({});
    } finally {
      setLoaded(true);
    }
  }, [cacheVersion]);

  useEffect(() => {
    queueMicrotask(() => fetchContent());
  }, [fetchContent]);

  // Merged = defaults + server overrides
  const merged = useMemo(() => deepMerge(DEFAULTS, data), [data]);

  // Inject theme CSS variables.
  // In LIGHT mode we only let `--kw-gold` (accent) be customized — the rest of
  // the palette is owned by index.css's `[data-theme="light"]` rules so the
  // light cream surfaces aren't forced back to the CMS dark hexes via inline
  // styles. Stale inline overrides for bg/surface/text/muted are cleared.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const t = merged.theme || {};
    const root = document.documentElement;
    const isLight = mode === "light";
    const setOrClear = (key, val) => {
      if (isLight) {
        root.style.removeProperty(key);
      } else if (val) {
        root.style.setProperty(key, val);
      }
    };
    setOrClear("--kw-bg", t.bg);
    setOrClear("--kw-surface", t.surface);
    setOrClear("--kw-text", t.text);
    setOrClear("--kw-muted", t.muted);
    // Gold accent is shared across both palettes — always honor CMS override.
    if (t.accent) root.style.setProperty("--kw-gold", t.accent);
  }, [merged.theme, mode]);

  /**
   * Read a value from the merged content.
   * Example paths:
   *   c("brand.name")
   *   c("contact.phone")
   *   c("theme.accent")
   *   c("home.stats.years")
   *   c("services.btp.label.fr") — bilingual via separate keys (.fr / .en)
   */
  const c = useCallback(
    (path, fallback = undefined) => {
      const v = getByPath(merged, path);
      return v === undefined ? fallback : v;
    },
    [merged],
  );

  /**
   * Bilingual reader: tries `${path}.${lang}`, falls back to `${path}.fr`,
   * then to the provided fallback.
   */
  const cl = useCallback(
    (path, lang, fallback = undefined) => {
      const v = getByPath(merged, `${path}.${lang}`);
      if (v !== undefined && v !== null && v !== "") return v;
      const vFr = getByPath(merged, `${path}.fr`);
      if (vFr !== undefined && vFr !== null && vFr !== "") return vFr;
      return fallback;
    },
    [merged],
  );

  const isVisible = useCallback(
    (sectionKey) => {
      const v = getByPath(merged, `sections.${sectionKey}`);
      return v === undefined ? true : Boolean(v);
    },
    [merged],
  );

  return (
    <SiteContentContext.Provider
      value={{
        loaded,
        data,
        merged,
        updatedAt,
        c,
        cl,
        isVisible,
        refresh: fetchContent,
      }}
    >
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error("useSiteContent must be used within SiteContentProvider");
  return ctx;
};

export { DEFAULTS };
